"use client"
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { type BreadcrumbEntityType, type BreadcrumbItem as BreadcrumbItemType, useBreadcumbs } from '@/components/breadcrumbs/use-breadcumbs';
import { Slash } from 'lucide-react';
import { Fragment } from 'react';
import { Link } from '@/i18n/navigation';
import { useShopById } from '@/app/admin/shops/_api/use-shop-queries';
import { useAdminCharity } from '@/app/admin/charities/_api/use-charity-queries';
import { useMerchantById } from '@/app/admin/merchants/_api/use-merchant-queries';
import { useAdminUser } from '@/app/admin/users/_api/use-user-admin-queries';
import { useProjectById } from '@/app/[locale]/projects/_api/use-project-queries';
import { useTranslations } from 'next-intl';

function EntityName({ entityType, entityId, charityId }: {
    entityType: BreadcrumbEntityType;
    entityId: string;
    charityId?: string;
}) {
    const t = useTranslations("common");
    const shop = useShopById(entityId, { enabled: entityType === 'shop' });
    const charity = useAdminCharity(entityId, { enabled: entityType === 'charity' });
    const parentCharity = useAdminCharity(charityId ?? '', { enabled: entityType === 'project' && !!charityId });
    const project = useProjectById(entityId, { enabled: entityType === 'project' && !charityId });
    const merchant = useMerchantById(entityId, { enabled: entityType === 'merchant' });
    const user = useAdminUser(entityId, { enabled: entityType === 'user' });

    switch (entityType) {
        case 'shop':
            return <>{shop.data?.name ?? t("breadcrumbs.details")}</>;
        case 'charity':
            return <>{(charity.data?.displayName ?? charity.data?.name) ?? t("breadcrumbs.details")}</>;
        case 'project': {
            if (charityId) {
                const nested = parentCharity.data?.projects?.find(p => p.id === entityId);
                return <>{nested?.name ?? t("breadcrumbs.details")}</>;
            }
            return <>{project.data?.name ?? t("breadcrumbs.details")}</>;
        }
        case 'merchant':
            return <>{merchant.data?.name ?? t("breadcrumbs.details")}</>;
        case 'user': {
            const u = user.data;
            if (!u) return <>{t("breadcrumbs.details")}</>;
            return <>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || t("breadcrumbs.details")}</>;
        }
        default:
            return <>{t("breadcrumbs.details")}</>;
    }
}

function BreadcrumbLabel({ item }: { item: BreadcrumbItemType }) {
    if (item.entityType && item.entityId) {
        return <EntityName entityType={item.entityType} entityId={item.entityId} charityId={item.charityId} />;
    }
    return <>{item.title}</>;
}

export function Breadcrumbs() {
    const items = useBreadcumbs();

    if (items.length === 0) return null;

    return (
        <Breadcrumb className="min-w-0 flex-1 overflow-hidden">
            <BreadcrumbList className="flex-nowrap">
                {items.map((item, index) => (
                    <Fragment key={item.link}>
                        {index !== items.length - 1 ? (
                            <>
                                <BreadcrumbItem className="hidden md:block whitespace-nowrap">
                                    <BreadcrumbLink asChild>
                                        <Link href={item.link}><BreadcrumbLabel item={item} /></Link>
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator className="hidden md:block shrink-0">
                                    <Slash/>
                                </BreadcrumbSeparator>
                            </>
                        ) : (
                            <BreadcrumbPage className="truncate">
                                <BreadcrumbLabel item={item} />
                            </BreadcrumbPage>
                        )}
                    </Fragment>
                ))}
            </BreadcrumbList>
        </Breadcrumb>
    );
}
